import React, {useRef, useState, useEffect, useCallback} from 'react';
import {Reorder} from 'framer-motion';
import {Box} from '@mui/material';
import {Button, Body, Icon} from '@lightricks/react-design-system';
import uploadAsset from '@/services/file-upload/uploadAsset';
import contentSubmissionsApi from '@/api/contentSubmissions';
import useCreateSubmission from '@/hooks/queries/mutations/use-create-submission';
import useReviseSubmission from '@/hooks/queries/mutations/use-revise-submission';
import translate from '@/utils/translate';
import validateFile from '../../validateFile';
import {trackDeliverables, captureDeliverablesError} from '../../telemetry';
import submissionErrorMessage from '../../submissionErrorMessage';
import {DeliverableSlot} from '../../types';
import VersionChat from '../../messages/VersionChat';
import styles from './MixedMediaEditor.module.scss';

interface Asset {
  id: string;
  kind: 'photo' | 'video';
  sourceUrl: string;
  previewUrl: string;
  note: string | null;
  weight: number;
  // The brand reviews each asset individually, so revision feedback lives per-child.
  brandMessage: string | null;
  latestVersionId: string | null;
}

interface Props {
  gigId: string;
  slot: DeliverableSlot;
  minCount: number;
  initialAssetId?: string | null;
  initialVersionId?: string | null;
  onDone: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  groupLabel?: string;
  readOnly?: boolean;
}

function kindFromMime(type: string): 'photo' | 'video' {
  return type.startsWith('video/') ? 'video' : 'photo';
}

export default function MixedMediaEditor({
  gigId,
  slot,
  minCount,
  initialAssetId,
  initialVersionId,
  onDone,
  onDirtyChange,
  groupLabel,
  readOnly,
}: Props) {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [openThreadId, setOpenThreadId] = useState<string | null>(
    initialAssetId ?? null
  );
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [childrenLoaded, setChildrenLoaded] = useState(!slot.submission);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialAssetsRef = useRef<Asset[] | null>(null);
  const nextIdRef = useRef(0);
  const objectUrlsRef = useRef<Set<string>>(new Set());
  const replaceIdRef = useRef<string | null>(null);
  const assetsRef = useRef(assets);
  assetsRef.current = assets;
  const [uploadingCount, setUploadingCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const busyRef = useRef(false);
  const uploadControllersRef = useRef<Set<AbortController>>(new Set());

  const create = useCreateSubmission(gigId);
  const revise = useReviseSubmission(gigId);

  const isEditing = Boolean(slot.submission);
  let readOnlyNote =
    'In review — you can make changes if the brand requests edits.';
  if (slot.state === 'approved') readOnlyNote = 'Approved by the brand.';
  else if (slot.state === 'archived') readOnlyNote = 'Archived.';

  useEffect(() => {
    if (!slot.submission) return;
    contentSubmissionsApi
      .children(slot.submission.id)
      .then(({data}) => {
        const seeded = data.map((child) => ({
          id: child.id,
          kind: (child.kind === 'video' ? 'video' : 'photo') as
            | 'photo'
            | 'video',
          sourceUrl: child.contentUrl ?? '',
          previewUrl: child.thumbnailUrl ?? child.contentUrl ?? '',
          note: child.note,
          weight: child.weight,
          brandMessage: child.brandMessage ?? null,
          latestVersionId: child.latestVersionId,
        }));
        seeded.sort((a, b) => a.weight - b.weight);
        setAssets(seeded);
        initialAssetsRef.current = seeded;
        setChildrenLoaded(true);
        trackDeliverables('Loaded existing mixed-media assets', {
          gigId,
          submissionId: slot.submission?.id,
          assetCount: seeded.length,
        });
      })
      .catch((err) => {
        captureDeliverablesError(err, 'Failed to load mixed-media assets', {
          gigId,
          submissionId: slot.submission?.id,
        });
        setLoadError(true);
      });
  }, [slot.submission, gigId]);

  useEffect(() => {
    const urls = objectUrlsRef.current;
    const controllers = uploadControllersRef.current;
    return () => {
      controllers.forEach((controller) => controller.abort());
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  useEffect(() => {
    trackDeliverables('Opened mixed-media editor', {
      gigId,
      submissionId: slot.submission?.id,
      editing: isEditing,
    });
  }, [gigId, slot.submission?.id, isEditing]);

  useEffect(() => {
    if (initialAssetId) setOpenThreadId(initialAssetId);
  }, [initialAssetId, initialVersionId]);

  const isDirty = useCallback(() => {
    if (!initialAssetsRef.current) return assets.length > 0;
    if (assets.length !== initialAssetsRef.current.length) return true;
    return assets.some(
      (a, i) => a.sourceUrl !== initialAssetsRef.current![i]?.sourceUrl
    );
  }, [assets]);

  useEffect(() => {
    onDirtyChange?.(isDirty());
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const handleClose = () => {
    if (
      isDirty() &&
      !window.confirm(translate('Discard your unsaved changes?'))
    ) {
      return;
    }
    onDone();
  };

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const replaceId = replaceIdRef.current;
    replaceIdRef.current = null;
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    // Seeded children overwrite assets; don't add until that fetch has finished.
    if (!childrenLoaded || busyRef.current) return;
    const v = validateFile(file, slot.kind);
    if (!v.valid) {
      setError(v.reason);
      return;
    }
    setError(null);
    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.add(previewUrl);
    const controller = new AbortController();
    uploadControllersRef.current.add(controller);
    setUploadingCount((count) => count + 1);
    try {
      const {sourceUrl} = await uploadAsset(file, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        URL.revokeObjectURL(previewUrl);
        objectUrlsRef.current.delete(previewUrl);
        return;
      }
      const current = replaceId
        ? assetsRef.current.find((asset) => asset.id === replaceId)
        : undefined;
      if (replaceId && !current) {
        URL.revokeObjectURL(previewUrl);
        objectUrlsRef.current.delete(previewUrl);
        return;
      }
      if (current?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(current.previewUrl);
        objectUrlsRef.current.delete(current.previewUrl);
      }
      setAssets((prev) => {
        if (replaceId) {
          if (!prev.some((asset) => asset.id === replaceId)) return prev;
          return prev.map((asset) =>
            asset.id === replaceId
              ? {
                  ...asset,
                  kind: kindFromMime(file.type),
                  sourceUrl,
                  previewUrl,
                }
              : asset
          );
        }
        const newId = `new-${nextIdRef.current}`;
        nextIdRef.current += 1;
        return [
          ...prev,
          {
            id: newId,
            kind: kindFromMime(file.type),
            sourceUrl,
            previewUrl,
            note: null,
            weight: prev.length,
            brandMessage: null,
            latestVersionId: null,
          },
        ];
      });
    } catch (err: any) {
      URL.revokeObjectURL(previewUrl);
      objectUrlsRef.current.delete(previewUrl);
      if (
        err?.name === 'CanceledError' ||
        err?.name === 'AbortError' ||
        controller.signal.aborted
      ) {
        return;
      }
      captureDeliverablesError(err, 'Mixed-media asset upload failed', {
        gigId,
        submissionId: slot.submission?.id,
      });
      setError('Upload failed. Try again.');
    } finally {
      uploadControllersRef.current.delete(controller);
      if (!controller.signal.aborted) {
        setUploadingCount((count) => count - 1);
      }
    }
  }

  const onReorder = (newOrder: Asset[]) => {
    if (busyRef.current) return;
    setAssets(newOrder.map((a, i) => ({...a, weight: i})));
  };

  function removeAsset(index: number) {
    if (busyRef.current) return;
    setAssets((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.previewUrl);
        objectUrlsRef.current.delete(removed.previewUrl);
      }
      return prev
        .filter((_, i) => i !== index)
        .map((a, i) => ({...a, weight: i}));
    });
  }

  function openAddPicker() {
    if (busyRef.current) return;
    replaceIdRef.current = null;
    inputRef.current?.click();
  }

  function openReplacePicker(id: string) {
    if (busyRef.current) return;
    replaceIdRef.current = id;
    inputRef.current?.click();
  }

  const onSubmit = async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    setIsSubmitting(true);
    const initialById = new Map(
      (initialAssetsRef.current ?? []).map((asset) => [asset.id, asset])
    );
    const childContentSubmissions = assets.map((a, i) => {
      const isTemp = a.id.startsWith('new-');
      const initial = initialById.get(a.id);
      const fileChanged = !initial || initial.sourceUrl !== a.sourceUrl;
      if (isTemp) {
        return {
          kind: a.kind,
          sourceUrl: a.sourceUrl,
          note: a.note,
          weight: i,
        };
      }
      return {
        id: a.id,
        kind: a.kind,
        ...(fileChanged ? {sourceUrl: a.sourceUrl} : {}),
        note: a.note,
        weight: i,
      };
    });

    try {
      if (isEditing && slot.submission) {
        await revise.mutateAsync({
          submissionId: slot.submission.id,
          payload: {childContentSubmissions},
        });
      } else {
        await create.mutateAsync({
          kind: 'mixed_media',
          deliverableKind: slot.deliverableKind,
          childContentSubmissions,
        });
      }
      trackDeliverables(
        isEditing ? 'Mixed-media revised' : 'Mixed-media created',
        {
          gigId,
          submissionId: slot.submission?.id,
          assetCount: assets.length,
        }
      );
      onDone();
    } catch (err) {
      captureDeliverablesError(err, 'Mixed-media submission failed', {
        gigId,
        submissionId: slot.submission?.id,
        assetCount: assets.length,
        editing: isEditing,
      });
      setError(submissionErrorMessage(err, 'Submission failed. Try again.'));
      setIsSubmitting(false);
    }
  };

  // Not-yet-processed children come back with a placeholder/missing URL; hide the broken
  // media so the neutral .thumb box shows instead of a broken-image icon.
  const hideBrokenMedia = (
    e: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement>
  ) => {
    e.currentTarget.style.display = 'none';
  };

  const renderThumb = (asset: Asset) => {
    if (asset.kind === 'video' && asset.previewUrl.startsWith('blob:')) {
      return (
        <video
          src={asset.previewUrl}
          muted
          playsInline
          onError={hideBrokenMedia}
        />
      );
    }
    if (asset.previewUrl) {
      return <img src={asset.previewUrl} alt="" onError={hideBrokenMedia} />;
    }
    return (
      <span className={styles.thumbIcon} aria-hidden>
        <Icon
          name={
            asset.kind === 'video'
              ? 'Navigation-VideoCamera-Line'
              : 'Navigation-Photos-Missing'
          }
          size="medium"
          appearance="secondary"
        />
      </span>
    );
  };

  const renderAssetBody = (asset: Asset, index: number) => (
    <>
      <div className={styles.thumb}>{renderThumb(asset)}</div>
      <div className={styles.assetText}>
        <span className={styles.assetLabel}>
          {asset.kind === 'video' ? 'Video' : 'Photo'} {index + 1}
        </span>
        {asset.brandMessage && (
          <span className={styles.assetFeedback}>
            The brand asked: {asset.brandMessage}
          </span>
        )}
        {asset.latestVersionId && (
          <button
            type="button"
            className={styles.assetMessages}
            aria-label={`Messages for ${
              asset.kind === 'video' ? 'Video' : 'Photo'
            } ${index + 1}`}
            aria-expanded={openThreadId === asset.id}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() =>
              setOpenThreadId((current) =>
                current === asset.id ? null : asset.id
              )
            }
          >
            <Icon
              name="Navigation-Messages"
              size="small"
              appearance="secondary"
            />
            Messages
          </button>
        )}
      </div>
    </>
  );

  const openThread = assets.find((asset) => asset.id === openThreadId);
  const openVersionId =
    openThread?.id === initialAssetId && initialVersionId
      ? initialVersionId
      : openThread?.latestVersionId;

  const isUploading = uploadingCount > 0;
  const isBusy =
    isUploading || isSubmitting || create.isLoading || revise.isLoading;
  busyRef.current = isBusy;
  const canSubmit = assets.length >= minCount && !isBusy && childrenLoaded;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          type="button"
          data-testid="mm-back"
          aria-label="Back"
          className={styles.backBtn}
          onClick={handleClose}
        >
          <Icon
            name="Actions-Arrow-Back-Large-Primary"
            size="medium"
            appearance="primary"
          />
        </button>
        <span className={styles.headerTitle}>
          {groupLabel ?? 'Story Uploads'}
        </span>
      </div>

      {slot.submission?.brandMessage && (
        <div className={styles.feedback} data-testid="revision-message">
          <Body size="xs" className={styles.feedbackLabel}>
            The brand requested changes
          </Body>
          <Body size="sm" className={styles.feedbackMessage}>
            {slot.submission.brandMessage}
          </Body>
        </div>
      )}

      {loadError && (
        <Body size="sm" className={styles.error} data-testid="mm-load-error">
          {translate(
            'Couldn’t load your existing files. Please reload the page.'
          )}
        </Body>
      )}

      {openThread && openVersionId && (
        <Box className={styles.thread} data-testid="mm-asset-thread">
          <Body size="sm" className={styles.threadTitle}>
            Messages about {openThread.kind === 'video' ? 'Video' : 'Photo'}{' '}
            {assets.indexOf(openThread) + 1}
          </Body>
          <VersionChat versionId={openVersionId} />
        </Box>
      )}

      {readOnly ? (
        <ul className={styles.grid} data-testid="mm-grid">
          {assets.map((asset, index) => (
            <li
              key={asset.id}
              className={`${styles.asset}${
                asset.brandMessage ? ` ${styles.assetFlagged}` : ''
              }`}
              data-testid="mm-asset"
            >
              {renderAssetBody(asset, index)}
            </li>
          ))}
        </ul>
      ) : (
        <Reorder.Group
          axis="y"
          values={assets}
          onReorder={onReorder}
          data-testid="mm-grid"
          className={styles.grid}
        >
          {assets.map((asset, index) => (
            <Reorder.Item
              key={asset.id}
              value={asset}
              data-testid="mm-asset"
              dragListener={!isBusy}
              className={`${styles.asset}${
                asset.brandMessage ? ` ${styles.assetFlagged}` : ''
              }`}
            >
              {renderAssetBody(asset, index)}
              <div className={styles.assetActions}>
                <button
                  type="button"
                  data-testid="mm-replace"
                  aria-label={`Replace ${
                    asset.kind === 'video' ? 'Video' : 'Photo'
                  } ${index + 1}`}
                  className={styles.replace}
                  disabled={isBusy}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={() => openReplacePicker(asset.id)}
                >
                  Replace
                </button>
                <button
                  type="button"
                  data-testid="mm-remove"
                  aria-label={`Remove ${
                    asset.kind === 'video' ? 'Video' : 'Photo'
                  } ${index + 1}`}
                  onClick={() => removeAsset(index)}
                  className={styles.remove}
                  disabled={isBusy}
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  Remove
                </button>
              </div>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      )}

      {readOnly ? (
        <div className={styles.footer}>
          <span className={styles.count} data-testid="mm-readonly-note">
            {readOnlyNote}
          </span>
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            data-testid="mm-add"
            type="file"
            accept="image/*,video/*"
            onChange={onPick}
            hidden
          />

          <button
            type="button"
            data-testid="mm-add-btn"
            className={styles.addZone}
            disabled={isBusy || !childrenLoaded}
            onClick={openAddPicker}
          >
            <span className={styles.addPlus} aria-hidden="true">
              +
            </span>
            Add photo or video
          </button>

          {error && (
            <Body size="xs" className={styles.error}>
              {error}
            </Body>
          )}

          <div className={styles.footer}>
            <span className={styles.count}>
              {assets.length} of {minCount}+ added
            </span>
            <Button
              testID="mm-submit"
              appearance="brand"
              mode="filled"
              size="large"
              disabled={!canSubmit}
              onClick={onSubmit}
            >
              {isUploading ? 'Uploading…' : 'Submit'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
